import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import usePrivacyContext from 'src/hooks/privacy/usePrivacyContext';
import { ContactFormData } from 'src/contexts/privacy/PrivacyContext';
import useAuth from 'src/hooks/useAuth';
import useWindowDimensions from 'src/hooks/useWindowDimensionHook';
import Page from 'src/components/Page';
import BoxTitlePage from 'src/theme/overrides/BoxTitlePage';
import { HEADER } from 'src/config';
import { getScrollbarStyles } from 'src/theme/scrollbar';

// ----------------------------------------------------------------------

const ContactCard = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3),
}));

const InfoRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
}));

const INITIAL_FORM: ContactFormData = {
  name: '',
  email: '',
  company: '',
  subject: '',
  message: '',
};

export default function PageContactUs() {
  const theme = useTheme();
  const { height } = useWindowDimensions();
  const { onHandleSendContactEmail, contactSending, contactSuccess, contactError } = usePrivacyContext();
  const { user } = useAuth();

  const [form, setForm] = useState<ContactFormData>({
    ...INITIAL_FORM,
    name: user ? `${user.name || ''} ${user.surname || ''}`.trim() : '',
    email: user?.email || '',
  });

  const handleChange = (field: keyof ContactFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isValid = form.name && form.email && form.subject && form.message;

  const handleSubmit = async () => {
    if (!isValid) return;
    await onHandleSendContactEmail(form);
  };

  const contentHeight = height - HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT - HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT - 70;

  return (
    <Page title="Privacy Contact">
      <BoxTitlePage>
        <Typography variant="h2">Privacy Contact</Typography>
      </BoxTitlePage>
      <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', height: contentHeight, overflowY: 'auto', ...getScrollbarStyles(theme) }}>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <ContactCard sx={{ height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2.5}>Invia un messaggio</Typography>
              {contactSuccess ? (
                <Stack alignItems="center" spacing={2} py={4}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
                  <Typography variant="h6" fontWeight={700} textAlign="center">Messaggio inviato!</Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Ti risponderemo all'indirizzo <strong>{form.email}</strong> il prima possibile.
                  </Typography>
                  <Button variant="outlined" size="small" onClick={() => setForm({ ...INITIAL_FORM, name: form.name, email: form.email })}>
                    Invia un altro messaggio
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <TextField label="Nome e Cognome" fullWidth size="small" value={form.name} onChange={handleChange('name')} required />
                    <TextField label="Email" fullWidth size="small" type="email" value={form.email} onChange={handleChange('email')} required />
                  </Stack>
                  <TextField label="Azienda" fullWidth size="small" value={form.company} onChange={handleChange('company')} />
                  <TextField
                    label="Oggetto" fullWidth size="small"
                    value={form.subject} onChange={handleChange('subject')} required
                    placeholder="Es. Richiesta informazioni DPIA"
                  />
                  <TextField
                    label="Messaggio" fullWidth size="small" multiline minRows={4}
                    value={form.message} onChange={handleChange('message')} required
                    placeholder="Descrivi la tua richiesta nel dettaglio..."
                  />
                  {contactError && <Alert severity="error" sx={{ borderRadius: 2 }}>{contactError}</Alert>}
                  <Button
                    variant="contained"
                    endIcon={contactSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    disabled={!isValid || contactSending}
                    onClick={handleSubmit}
                    sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', px: 3 }}
                  >
                    {contactSending ? 'Invio in corso...' : 'Invia messaggio'}
                  </Button>
                </Stack>
              )}
            </ContactCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <ContactCard>
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Richieste Privacy</Typography>
                <InfoRow>
                  <EmailOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email DPO</Typography>
                    <Typography variant="body2" fontWeight={600}>privacy@blimp.ai</Typography>
                  </Box>
                </InfoRow>
                <InfoRow>
                  <PhoneOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Telefono</Typography>
                    <Typography variant="body2" fontWeight={600}>02 45902000</Typography>
                  </Box>
                </InfoRow>
                <InfoRow>
                  <LocationOnOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Sede</Typography>
                    <Typography variant="body2" fontWeight={600}>Viale Monza 259, 20126 Milano</Typography>
                  </Box>
                </InfoRow>
              </ContactCard>

              <ContactCard>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>Risorse utili</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1}>
                  {[
                    { label: 'FAQ Privacy', href: 'https://blimp.ai/faq/' },
                    { label: 'Privacy Policy', href: 'https://blimp.ai/privacy-policy/' },
                    { label: 'Privacy by Design', href: 'https://blimp.ai/privacy-compliant/' },
                    { label: 'Cookie Policy', href: 'https://blimp.ai/cookie-policy/' },
                  ].map((link) => (
                    <Typography
                      key={link.label} component="a" href={link.href}
                      target="_blank" rel="noopener noreferrer"
                      variant="body2" color="primary.main"
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      → {link.label}
                    </Typography>
                  ))}
                </Stack>
              </ContactCard>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
}